import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { ButtonLink, cx, SyntheticBanner } from "./ui";
import { useSession, workspaceHome } from "../lib/session";
import { useLang } from "../lib/i18n";
import { LangToggle } from "./LangToggle";

export function PublicLayout() {
  const { user, config } = useSession();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const dark = loc.pathname === "/";

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const overHero = dark && !scrolled && !open;
  const links = [
    { to: "/feed", label: t("nav_feed") },
    { to: "/record", label: t("nav_record") },
    { to: "/promises", label: t("nav_promises") },
    { to: "/constituencies", label: t("nav_constituencies") },
    { to: "/join", label: t("nav_join") },
    { to: "/about", label: t("nav_about") },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {config && config.environment !== "production" && <SyntheticBanner />}
      <header className={cx("sticky top-0 z-40 transition-all duration-300", overHero ? "bg-transparent" : "glass border-b hairline")}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8">
          <Link to="/" aria-label="Seva Sangrah home">
            <Wordmark light={overHero} />
          </Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cx("rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors", overHero ? "text-white/80 hover:text-white" : isActive ? "text-ink-900 bg-ink-50" : "text-ink-600 hover:text-ink-900")
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <LangToggle light={overHero} />
            {user ? (
              <ButtonLink to={workspaceHome(user.workspace)} variant={overHero ? "light" : "secondary"} size="sm">
                {user.workspace === "citizen" ? t("nav_account") : t("nav_workspace")}
              </ButtonLink>
            ) : (
              <ButtonLink to="/signin" variant={overHero ? "light" : "secondary"} size="sm">
                {t("nav_signin")}
              </ButtonLink>
            )}
            <ButtonLink to="/complaint" variant="saffron" size="sm">
              {t("nav_report")}
            </ButtonLink>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <LangToggle light={overHero} />
            <button className={cx("rounded-full p-2 cursor-pointer", overHero ? "text-white" : "text-ink-800")} onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t hairline bg-paper px-5 pb-6 pt-2 md:hidden">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="block border-b hairline py-3.5 text-[17px] font-medium text-ink-900">
                {l.label}
              </Link>
            ))}
            <div className="mt-5 flex flex-col gap-2">
              <ButtonLink to="/complaint" variant="saffron" size="lg">
                {t("nav_report")}
              </ButtonLink>
              <ButtonLink to={user ? workspaceHome(user.workspace) : "/signin"} variant="secondary" size="lg">
                {user ? t("nav_account") : t("nav_signin")}
              </ButtonLink>
            </div>
          </div>
        )}
      </header>
      <main className={cx("flex-1", dark ? "-mt-16" : "")}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <div className="eyebrow text-ink-400">{title}</div>
      <ul className="mt-4 space-y-2.5">
        {links.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-[14px] text-ink-200 hover:text-white transition-colors">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const { tx } = useLang();
  return (
    <footer className="bg-ink-950 text-white">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div>
            <Wordmark light />
            <p className="mt-5 max-w-sm text-[14px] leading-relaxed text-ink-300">
              {tx(
                "People are sovereign. They delegate authority. Seva Sangrah remembers what happens — so that memory creates visibility, visibility creates accountability, and accountability creates learning.",
                "असली ताक़त जनता के पास है। जनता ही नेताओं और अधिकारियों को ताक़त सौंपती है। सेवा संग्रह याद रखता है कि उस ताक़त का क्या हुआ — क्योंकि याद रखने से सच सामने आता है, सच सामने आने से जवाबदेही बनती है, और जवाबदेही से सुधार होता है।",
              )}
            </p>
          </div>
          <FooterCol title={tx("Act", "कुछ करें")} links={[["/complaint", tx("Register a complaint", "शिकायत दर्ज करें")], ["/promise/new", tx("Record a promise", "वादा दर्ज करें")], ["/complaint/track", tx("Track a complaint", "अपनी शिकायत का हाल देखें")]]} />
          <FooterCol title={tx("The record", "रिकॉर्ड")} links={[["/feed", tx("Problems near you", "आस-पास की समस्याएँ")], ["/record", tx("Civic record", "जनता का रिकॉर्ड")], ["/promises", tx("Promise registry", "वादों की सूची")], ["/constituencies", tx("Constituencies", "चुनाव क्षेत्र")], ["/representatives", tx("Representatives", "जनप्रतिनिधि")]]} />
          <FooterCol title={tx("Join", "जुड़िए")} links={[["/join/founding-citizen", tx("Founding Citizen", "संस्थापक नागरिक")], ["/join/expert", tx("Expert Contributor", "विशेषज्ञ के रूप में")], ["/join/civil-society", tx("Civil Society / NGO", "सामाजिक संगठन / NGO")], ["/connect", tx("Connect with us", "हमसे संपर्क करें")]]} />
          <FooterCol title={tx("Institution", "संस्था")} links={[["/about", tx("About", "हमारे बारे में")], ["/standards", tx("Record standards", "रिकॉर्ड के नियम")], ["/privacy", tx("Privacy", "निजता (प्राइवेसी)")], ["/terms", tx("Terms", "शर्तें")], ["/recover", tx("Recover access", "खाते में दोबारा पहुँचें")]]} />
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-[12.5px] leading-relaxed text-ink-400 md:flex-row md:items-center md:justify-between">
          <p className="max-w-3xl">
            {tx(
              "Seva Sangrah is not a government authority, police authority or court. It preserves citizens' experiences, routes matters to the institutions with lawful authority, and records what happens. A complaint is a citizen's account, not proof of wrongdoing.",
              "सेवा संग्रह कोई सरकारी दफ़्तर, पुलिस या अदालत नहीं है। हम लोगों की आपबीती को सँभालकर रखते हैं, मामले को उस विभाग तक पहुँचाते हैं जिसके पास कार्रवाई का अधिकार है, और फिर जो होता है उसे दर्ज करते हैं। शिकायत किसी नागरिक का अपना बयान है — इससे अपने-आप किसी की ग़लती साबित नहीं होती।",
            )}
          </p>
          <p className="shrink-0">© {new Date().getFullYear()} {tx("Seva Sangrah", "सेवा संग्रह")}</p>
        </div>
      </div>
    </footer>
  );
}

export function PageShell({ children, narrow }: { children: ReactNode; narrow?: boolean }) {
  return <div className={cx("mx-auto px-5 py-12 sm:px-8 sm:py-16", narrow ? "max-w-3xl" : "max-w-7xl")}>{children}</div>;
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-0.5 font-medium text-ink-800 underline decoration-ink-200 underline-offset-4 hover:decoration-ink-800">
      {children}
      <ArrowUpRight className="h-3.5 w-3.5" />
    </a>
  );
}
